import { useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { PasswordInput } from '../components/ui/PasswordInput';
import { FormField } from '../components/ui/FormField';
import { AuthLayout, AuthBenefit } from '../components/layout/AuthLayout';
import { AuthFormError, authErrorMessage } from '../components/layout/AuthFormError';
import { formatPhoneMask } from '../utils/phone';
import {
  MIN_PASSWORD_LENGTH,
  RegisterFormErrors as FormErrors,
  RegisterFormState as FormState,
  validateRegisterForm as validateForm,
} from '../utils/registerValidation';
import { useRegisterAccount } from '../hooks/useRegisterAccount';
import { forwardState, returnPathFrom } from '../utils/authRedirect';

const BENEFITS: AuthBenefit[] = [
  { icon: 'orders', label: 'Acompanhe cada pedido', description: 'Status e próximo passo de tudo o que você comprar.' },
  { icon: 'checkout', label: 'Dados prontos no checkout', description: 'Nas próximas compras, seus dados já vêm preenchidos.' },
  { icon: 'heart', label: 'Favorite peças', description: 'Guarde as peças que você gostou para decidir depois.' },
];

export function RegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const registerAccount = useRegisterAccount();
  const formRef = useRef<HTMLFormElement>(null);

  const [form, setForm] = useState<FormState>({ name: '', email: '', phone: '', password: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Partial<Record<keyof FormState, boolean>>>({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const updateField = (field: keyof FormState, value: string) => {
    const next = { ...form, [field]: value };
    setForm(next);
    setSubmitError('');
    if (touched[field]) setErrors(validateForm(next));
  };

  const handleBlur = (field: keyof FormState) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setErrors(validateForm(form));
  };

  const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();

    setTouched({ name: true, email: true, phone: true, password: true });
    const validationErrors = validateForm(form);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      requestAnimationFrame(() => {
        formRef.current?.querySelector<HTMLInputElement>('[aria-invalid="true"]')?.focus();
      });
      return;
    }

    setLoading(true);
    setSubmitError('');
    try {
      await registerAccount(form);
      navigate(returnPathFrom(location.state), { replace: true });
    } catch (err) {
      setSubmitError(authErrorMessage(err, 'Não foi possível criar sua conta agora. Tente de novo.'));
    } finally {
      setLoading(false);
    }
  };

  const fieldError = (field: keyof FormState) => (touched[field] ? errors[field] : undefined);

  return (
    <AuthLayout
      heading={<>Looks pensados <span className="text-chocolate">com carinho</span> para quem você ama</>}
      description="Crie sua conta para acompanhar seus pedidos e comprar mais rápido da próxima vez."
      benefits={BENEFITS}
    >
      <div className="mb-7">
        <h1 className="font-display text-3xl font-bold text-text-dark">Criar conta</h1>
        <p className="text-text-medium text-base mt-1">Todos os campos são obrigatórios.</p>
      </div>

      <form ref={formRef} onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
        <FormField label="Nome completo" error={fieldError('name')}>
          <Input
            value={form.name}
            onChange={(e) => updateField('name', e.target.value)}
            onBlur={handleBlur('name')}
            placeholder="Maria Silva"
            autoComplete="name"
          />
        </FormField>

        <FormField label="E-mail" error={fieldError('email')}>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => updateField('email', e.target.value)}
            onBlur={handleBlur('email')}
            placeholder="maria@email.com"
            autoComplete="email"
            autoCapitalize="none"
          />
        </FormField>

        <FormField label="Celular" error={fieldError('phone')}>
          <Input
            type="tel"
            inputMode="numeric"
            value={form.phone}
            onChange={(e) => updateField('phone', formatPhoneMask(e.target.value))}
            onBlur={handleBlur('phone')}
            placeholder="(85) 99999-9999"
            autoComplete="tel"
          />
        </FormField>

        <FormField
          label="Senha"
          hint={fieldError('password') ? undefined : `Use pelo menos ${MIN_PASSWORD_LENGTH} caracteres.`}
          error={fieldError('password')}
        >
          <PasswordInput
            value={form.password}
            onChange={(e) => updateField('password', e.target.value)}
            onBlur={handleBlur('password')}
            autoComplete="new-password"
          />
        </FormField>

        {submitError && (
          <AuthFormError>
            <p>{submitError}</p>
          </AuthFormError>
        )}

        <Button type="submit" fullWidth loading={loading} size="lg">
          {loading ? 'Criando conta...' : 'Criar conta'}
        </Button>
      </form>

      <p className="text-center text-base text-text-medium mt-7">
        Já tem conta?{' '}
        <Link to="/login" state={forwardState(location.state)} className="text-chocolate-dark font-semibold hover:underline underline-offset-4">
          Entrar
        </Link>
      </p>
    </AuthLayout>
  );
}
